import { fromJsonString } from '@bufbuild/protobuf';
import { rawColors } from '@repo/bytes-core/colors/raw-colors';
import { AgentEmbedConfig_Theme, AgentEmbedConfigSchema } from '@/gen/proto/cloud_agent_embed_pb';
export const EMBED_DEFAULT_COLOR = rawColors.blue['500'];
// Applied when both the server response and the snippet override are empty.
const DEFAULTS = {
    color: EMBED_DEFAULT_COLOR,
    logo: '',
    listeningText: 'Listening…',
    theme: 'system',
};
export async function fetchServerConfig(apiBase, cloudAgentId) {
    let response;
    try {
        response = await fetch(`${apiBase}/api/embed/config/${encodeURIComponent(cloudAgentId)}`, {
            method: 'GET',
            credentials: 'omit',
        });
    }
    catch (error) {
        return { status: 'error', error };
    }
    if (response.status === 403 || response.status === 404) {
        return { status: 'denied' };
    }
    if (!response.ok) {
        return { status: 'error', error: new Error(`unexpected status ${response.status}`) };
    }
    let body;
    try {
        body = await response.json();
    }
    catch (error) {
        return { status: 'error', error };
    }
    const config = parseServerConfig(body);
    if (config === null) {
        return { status: 'error', error: new Error('config response did not match expected shape') };
    }
    return { status: 'ok', config };
}
// cloud-api-server emits theme enum values lowercased, but protobuf-es's
// fromJson expects the proto3 JSON spec's uppercase names — a string
// mismatch silently falls back to the default enum value (interpreting
// "light" as SYSTEM). Pre-uppercase before parsing.
function parseServerConfig(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return null;
    const normalised = JSON.stringify(value).replace(/"theme"\s*:\s*"(system|light|dark)"/i, (_, name) => `"theme":"${name.toUpperCase()}"`);
    let parsed;
    try {
        parsed = fromJsonString(AgentEmbedConfigSchema, normalised, { ignoreUnknownFields: true });
    }
    catch {
        return null;
    }
    return {
        color: parsed.color,
        logo: parsed.logo,
        listening_text: parsed.listeningText,
        theme: themeFromEnum(parsed.theme),
        supports_chat: parsed.supportsChat,
        supports_video: parsed.supportsVideo,
        supports_screenshare: parsed.supportsScreenshare,
        agent_name: parsed.agentName,
    };
}
function themeFromEnum(theme) {
    switch (theme) {
        case AgentEmbedConfig_Theme.LIGHT:
            return 'light';
        case AgentEmbedConfig_Theme.DARK:
            return 'dark';
        default:
            return 'system';
    }
}
export function resolveConfig(server, parsed) {
    return {
        color: parsed.cosmetic.color ?? (server.color || DEFAULTS.color),
        logo: parsed.cosmetic.logo ?? (server.logo || DEFAULTS.logo),
        listeningText: parsed.cosmetic.listeningText ?? (server.listening_text || DEFAULTS.listeningText),
        theme: parsed.cosmetic.theme ?? (server.theme || DEFAULTS.theme),
        supportsChat: server.supports_chat,
        supportsVideo: server.supports_video,
        supportsScreenshare: server.supports_screenshare,
        agentName: server.agent_name,
        perSession: parsed.perSession,
    };
}
export function asHexColor(value) {
    return value.startsWith('#') ? `#${value.slice(1)}` : undefined;
}
export function resolveTheme(configured) {
    if (configured === 'light' || configured === 'dark')
        return configured;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
        return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
/** No-ops when the configured theme is fixed (light/dark). */
export function watchSystemTheme(configured, onChange) {
    if (configured !== 'system')
        return () => { };
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
        return () => { };
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (event) => onChange(event.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
}
